import {inject, injectable} from 'inversify';

import type {CourseApiModel} from '../../../../api/course';
import type {ProgramExpandedCommon} from '../../../../api/program';
import type {UserExpandedCommon} from '../../../../api/user';
import type {IApiDataMapper} from '../../../ports/types';
import type {CourseModel} from '../Course.model';

import {not} from '../../../../utils/conditionalUtils';
import {
  getCourseDurationString,
  isCourseArchived,
  isCourseEnded,
  isCourseIndividual,
  isCourseStarted,
} from '../../../../utils/entity/course';
import {CurrentUserService} from '../../../system/currentUser/CurrentUser.service';
import {EnvProviderService} from '../../../system/env/EnvProvider.service';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {EditionMapperJrpc} from '../../Edition/mappers/Edition.mapper.jrpc';
import {ProgramMapperJrpc} from '../../Program/mappers/Program.mapper.jrpc';
import {ProgramFactory} from '../../Program/Program.factory';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {CourseFactory} from '../Course.factory';
import {CourseExpandJrpc} from './Course.expand.jrpc';
import {CourseRestartMapperJrpc} from './CourseRestart.mapper.jrpc';

@injectable()
export class CourseMapperJrpc
  implements
    IApiDataMapper<CourseMapperJrpc.ApiData, CourseModel.Props, CourseModel>
{
  constructor(
    @inject(CourseFactory)
    private readonly factory: CourseFactory,
    @inject(CourseExpandJrpc)
    public readonly expand: CourseExpandJrpc,
    @inject(CurrentUserService)
    private readonly currentUserService: CurrentUserService,
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
    @inject(EnvProviderService)
    private readonly envProviderService: EnvProviderService,
    @inject(ProgramFactory) private readonly programFactory: ProgramFactory,
    @inject(ProgramMapperJrpc)
    private readonly programMapper: ProgramMapperJrpc,
    @inject(UserMapperJrpc) private readonly userMapper: UserMapperJrpc,
    @inject(EditionMapperJrpc)
    private readonly editionMapper: EditionMapperJrpc,
  ) {}

  mapDataToModel(apiData: CourseMapperJrpc.ExpandedApiData): CourseModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: CourseMapperJrpc.ExpandedApiData): CourseModel.Props {
    const id = apiData._id;
    const coachIds = this.coachIds(apiData);
    const isMeCoach = this.isMeCoach(apiData);

    const isStarted = this.isStarted(apiData);
    const isEnded = this.isEnded(apiData);
    const isArchived = this.isArchived(apiData);
    const isIndividual = this.isIndividual(apiData);

    const isAccessFrozen = this.isAccessFrozen({
      id,
      isMeCourseCoach: isMeCoach,
    });

    const startDate = this.startDate(apiData);
    const endDate = this.endDate(apiData);
    const clientsCount = this.clientsCount(apiData);
    const isOneToOne = this.isOneToOne(apiData);

    const program = this.program(apiData);
    const client = this.client(apiData);
    const avatar = this.avatar({program});
    const title = this.title({
      apiData,
      client,
      isIndividual,
      isMeCoach,
      program,
    });
    const label = this.label({apiData, isMeCoach});
    const durationString = this.durationString(apiData);
    const channelIds = this.channelIds(apiData);
    const restart = this.restart(apiData);
    const isAutoRestarting = this.isAutoRestarting(apiData);
    const updatedDate = this.updatedDate(apiData);
    const numberOfCycles = this.numberOfCycles(apiData);
    const currentCycle = this.currentCycle(apiData);
    const currentUsedOfferId = this.currentUsedOfferId(apiData);
    const programEndDate = this.programEndDate(apiData);
    const edition = this.edition(apiData);
    const usedOfferIds = this.usedOfferIds(apiData);

    return {
      apiData,
      avatar,
      channelIds,
      client,
      clientsCount,
      coachIds,
      currentCycle,
      currentUsedOfferId,
      durationString,
      edition,
      endDate,
      id,
      isAccessFrozen,
      isArchived,
      isAutoRestarting,
      isEnded,
      isIndividual,
      isMeCoach,
      isOneToOne,
      isStarted,
      label,
      numberOfCycles,
      program,
      programEndDate,
      restart,
      startDate,
      title,
      updatedDate,
      usedOfferIds,
    };
  }

  private avatar(props: {
    program: CourseModel.Props['program'];
  }): CourseModel.Props['avatar'] {
    return props.program.avatar;
  }

  private channelIds(props: CourseMapperJrpc.ApiData) {
    return props.channel_ids;
  }

  private client(
    props: CourseMapperJrpc.ExpandedApiData,
  ): CourseModel.Props['client'] {
    if (props.client) {
      return this.userMapper.mapDataToModel(props.client as UserExpandedCommon);
    }

    return null;
  }

  private clientsCount(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['clientsCount'] {
    return props.counters.memberships.accepted;
  }

  private coachIds(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['coachIds'] {
    return props.coach_ids ?? [];
  }

  private currentCycle(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['currentCycle'] {
    return props.current_cycle || 1;
  }

  private currentUsedOfferId(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['currentUsedOfferId'] {
    return props.current_offer_id || null;
  }

  private durationString(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['durationString'] {
    return getCourseDurationString(props);
  }

  private edition(
    props: CourseMapperJrpc.ExpandedApiData,
  ): CourseModel.Props['edition'] {
    if (props.edition) {
      return this.editionMapper.mapDataToModel(props.edition);
    }

    return null;
  }

  private endDate(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['endDate'] {
    return props.end_date
      ? this.dateTimeModelFactory.createFromDateTimeObject(props.end_date)
      : null;
  }

  private isAccessFrozen(props: {
    id: string;
    isMeCourseCoach: boolean;
  }): CourseModel.Props['isAccessFrozen'] {
    const {id, isMeCourseCoach} = props;

    return (
      not(isMeCourseCoach) && this.currentUserService.getIsMeFrozenForCourse(id)
    );
  }

  private isArchived(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isArchived'] {
    return isCourseArchived(props);
  }

  private isAutoRestarting(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isAutoRestarting'] {
    return Boolean(props.is_autorestarting);
  }

  private isEnded(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isEnded'] {
    return isCourseEnded(props);
  }

  private isIndividual(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isIndividual'] {
    return isCourseIndividual(props);
  }

  private isMeCoach(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isMeCoach'] {
    return this.currentUserService.isCoachForEntity(props);
  }

  private isOneToOne(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isOneToOne'] {
    return this.isIndividual(props);
  }

  private isStarted(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['isStarted'] {
    return isCourseStarted(props);
  }

  private label(props: {
    apiData: CourseMapperJrpc.ApiData;
    isMeCoach: boolean;
  }): CourseModel.Props['label'] {
    const {apiData, isMeCoach} = props;
    const courseLabel = apiData.label;

    if (isMeCoach && courseLabel) {
      return courseLabel;
    }

    return '';
  }

  private numberOfCycles(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['numberOfCycles'] {
    return props.num_cycles || 1;
  }

  private program(
    props: CourseMapperJrpc.ExpandedApiData,
  ): CourseModel.Props['program'] {
    if (props.program) {
      return this.programMapper.mapDataToModel(
        props.program as ProgramExpandedCommon,
      );
    }

    return this.programFactory.createEmpty();
  }

  private programEndDate(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['programEndDate'] {
    return props.program_end_date
      ? this.dateTimeModelFactory.createFromDateTimeObject(
          props.program_end_date,
        )
      : null;
  }

  private restart(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['restart'] {
    return new CourseRestartMapperJrpc(this.dateTimeModelFactory).map(
      props,
      this.envProviderService.app.isCore,
    );
  }

  private startDate(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['startDate'] {
    return props.start_date
      ? this.dateTimeModelFactory.createFromDateTimeObject(props.start_date)
      : null;
  }

  private title(props: {
    apiData: CourseMapperJrpc.ApiData;
    client: CourseModel.Props['client'];
    isIndividual: boolean;
    isMeCoach: boolean;
    program: CourseModel.Props['program'];
  }): CourseModel.Props['title'] {
    // todo: move to chat logic
    // const {apiData, isMeCoach, isIndividual, client, program} = props;
    // const course = apiData;
    //
    // let title = program?.title;
    //
    // if (isMeCoach && isIndividual) {
    //   const clientName = client?.name || 'Client not found';
    //
    //   if (this.envProviderService.app.isB2b) {
    //     title = clientName;
    //
    //     if (course?.client?.subtenant) {
    //       // TODO: subtenant for ApiUserModel??
    //       title += ` (${course?.client?.subtenant.title}/${
    //         course?.client?.subtenant.tenant?.title || 'Tenant not found'
    //       })`;
    //     }
    //   } else {
    //     title += ` (${clientName})`;
    //   }
    //
    //   return title;
    // }
    //
    // return title;

    return props.program?.title || '';
  }

  private updatedDate(
    props: CourseMapperJrpc.ApiData,
  ): CourseModel.Props['startDate'] {
    return props.updated
      ? this.dateTimeModelFactory.createFromDateTimeObject(props.updated)
      : null;
  }

  private usedOfferIds(
    props: CourseMapperJrpc.ExpandedApiData,
  ): CourseModel.Props['usedOfferIds'] {
    return props.used_offer_ids ?? [];
  }
}

export namespace CourseMapperJrpc {
  export type ApiData = CourseApiModel;

  export type ExpandedApiData = ApiData & CourseExpandJrpc.ExpandedFields;
}
